import { useState } from 'react';

import {
    formatOutreachResponse,
    getInboundCallActionPatientLabel,
} from '@apps/web/components/caregivers/caregiver_inbound_call_timeline_utils';
import { ShiftPageDetailsDialog } from '@apps/web/components/dashboard/shift_list_pane';
import {
    StatusBadge,
    type StatusConfig,
} from '@apps/web/components/ui/status_badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { formatCallBadgeLabel } from '@apps/web/components/voice_calls/voice_call_format_utils';
import { type ApiResponseType } from '@libraries/typescript/api_client';

type CallAction = ApiResponseType<
    '/organizations/{organization_id}/voice-calls/{voice_call_id}',
    'get'
>['actions'][number];

function getCallActionStatusConfig(action: CallAction): StatusConfig {
    if (action.type === 'shift_confirmation') {
        switch (action.outcome) {
            case 'confirm':
                return {
                    label: 'Confirmed',
                    dotColor: 'bg-green-500',
                    backgroundColor: 'bg-green-100 dark:bg-green-950/50',
                    textColor: 'text-green-800 dark:text-green-300',
                };
            case 'decline':
                return {
                    label: 'Declined',
                    dotColor: 'bg-red-500',
                    backgroundColor: 'bg-red-100 dark:bg-red-950/50',
                    textColor: 'text-red-800 dark:text-red-300',
                };
            case 'unclear':
                return {
                    label: 'Needs Review',
                    dotColor: 'bg-amber-500',
                    backgroundColor: 'bg-amber-100 dark:bg-amber-950/50',
                    textColor: 'text-amber-800 dark:text-amber-300',
                };
            default:
                return {
                    label: action.outcome
                        ? formatCallBadgeLabel(action.outcome)
                        : 'Updated',
                    dotColor: 'bg-slate-400',
                    backgroundColor: 'bg-muted',
                    textColor: 'text-muted-foreground',
                };
        }
    }

    if (action.type === 'outreach_response') {
        switch (action.response) {
            case 'interested':
            case 'available_interested':
                return {
                    label: 'Available',
                    dotColor: 'bg-green-500',
                    backgroundColor: 'bg-green-100 dark:bg-green-950/50',
                    textColor: 'text-green-800 dark:text-green-300',
                };
            case 'not_interested':
            case 'available_not_interested':
            case 'unavailable':
                return {
                    label: 'Unavailable',
                    dotColor: 'bg-red-500',
                    backgroundColor: 'bg-red-100 dark:bg-red-950/50',
                    textColor: 'text-red-800 dark:text-red-300',
                };
            case 'needs_review':
            case 'unknown':
                return {
                    label: 'Needs Review',
                    dotColor: 'bg-amber-500',
                    backgroundColor: 'bg-amber-100 dark:bg-amber-950/50',
                    textColor: 'text-amber-800 dark:text-amber-300',
                };
            case 'pending':
            case 'no_response':
                return {
                    label: 'Pending',
                    dotColor: 'bg-slate-400',
                    backgroundColor: 'bg-muted',
                    textColor: 'text-muted-foreground',
                };
            default:
                return {
                    label: formatOutreachResponse(action.response),
                    dotColor: 'bg-slate-400',
                    backgroundColor: 'bg-muted',
                    textColor: 'text-muted-foreground',
                };
        }
    }

    if (action.type === 'transfer') {
        return {
            label: 'Transferred',
            dotColor: 'bg-slate-400',
            backgroundColor: 'bg-muted',
            textColor: 'text-muted-foreground',
        };
    }

    return {
        label: 'Action Recorded',
        dotColor: 'bg-slate-400',
        backgroundColor: 'bg-muted',
        textColor: 'text-muted-foreground',
    };
}

function getCallActionContextLabel(action: CallAction): string {
    return getInboundCallActionPatientLabel(action);
}

export function CallActionsTable({
    organizationId,
    actions,
}: {
    readonly organizationId: string;
    readonly actions: Array<CallAction>;
}) {
    const [selectedShiftId, setSelectedShiftId] = useState<string | null>(null);
    const visibleActions = actions.filter(
        (action) => action.type !== 'transfer',
    );

    if (visibleActions.length === 0) {
        return null;
    }

    return (
        <div className="rounded-lg border">
            <Table className="table-fixed">
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-1/2">Context</TableHead>
                        <TableHead className="w-1/2">Action / Result</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {visibleActions.map((action, index) => (
                        <TableRow key={`action-${index}-${action.type}`}>
                            <TableCell className="font-medium whitespace-normal">
                                {action.shift_id ? (
                                    <button
                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                        onClick={() =>
                                            setSelectedShiftId(
                                                action.shift_id ?? null,
                                            )
                                        }
                                        type="button"
                                    >
                                        {getCallActionContextLabel(action)}
                                    </button>
                                ) : (
                                    getCallActionContextLabel(action)
                                )}
                            </TableCell>
                            <TableCell className="whitespace-normal">
                                <StatusBadge
                                    status={getCallActionStatusConfig(action)}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <ShiftPageDetailsDialog
                isOpen={selectedShiftId !== null}
                onClose={() => setSelectedShiftId(null)}
                organizationId={organizationId}
                shiftId={selectedShiftId}
            />
        </div>
    );
}
